/**
 * Getters para componentes @ui-kit (Header, Menu, Dashboard) en flujo v3.
 * Usa `i18n.get` cuando exista clave en backend; si no, texto de respaldo
 * (catálogo: `Client_00000000_0000_000_es.properties`).
 */
import * as i18n from "util/i18n";
import { getTransferInternalTitleLabel } from "util/transferInternalTitleLabels";

import NAV_RAIL_LABEL_FALLBACK from "v3/navRailLabelFallback";

/** @param {string} t */
function isMissingI18nKey(t) {
    return typeof t === "string" && t.startsWith("*") && t.endsWith("*");
}

const GREETING_FALLBACK = {
    greeting: "Hola",
    "greeting.suffix": "| Valor es conocerte",
};

const HEADER_LABEL_FALLBACK = {
    "header.goBack.aria": "volver",
    "header.goToMessagesAria": "Ir a mensajería",
    "header.switch.environment.aria": "Cambiar ambiente",
    "header.showAmounts.aria": "Mostrar importes",
    "header.hideAmounts.aria": "Ocultar importes",
    "header.mobileContextMenu.sheetTitle": "Opciones",
};

const USER_MENU_FALLBACK = {
    "header.user.menu.goToConfig": "Configuración",
    "header.user.menu.goToAccountOfficial": "Cuenta oficial",
    "header.user.menu.lastConnection": "Última conexión",
    "header.user.menu.handleLogout": "Cerrar sesión",
};

const MENU_LABEL_FALLBACK = {
    "menu.navAriaLabel": "navegación principal",
    "menu.bottomNavMore": "Más",
    "menu.featuredAction.sheetTitle": "Acción destacada",
    "menu.featuredAction.sheetIntro":
        "Al seleccionar una acción, quedará fija en la barra de navegación. Podés cambiarla manteniendo presionado el ícono durante 3 segundos.",
};

const MENU_TEXT_FALLBACK = {
    "menu.flyoutSubmenuNavigationAria": "{{parentLabel}} submenú",
};

/** Etiquetas de tabs y página en detalle de cuenta v3 (`AccountDetailV3Page`). */
const ACCOUNT_DETAIL_LABEL_FALLBACK = {
    "accounts.v3.detail.tab.movements": "Movimientos",
    "accounts.v3.detail.tab.information": "Información",
    "accounts.v3.detail.tab.extracts": "Resúmenes",
    "accounts.v3.detail.title.edit": "Editar",
    "accounts.v3.detail.title.edit.form.name": "Editar nombre de la cuenta",
    "accounts.v3.detail.title.edit.form.alias": "Editar alias",
    "accounts.v3.detail.form.account.name": "Nombre",
    "accounts.v3.detail.form.account.alias": "Alias",
    "accounts.v3.detail.form.account.name.required": "El campo es requerido",
    "accounts.v3.detail.form.account.name.maxLength": "El nombre no puede contener más de 50 caracteres",
    "accounts.v3.detail.form.account.alias.maxLength": "El alias no puede contener más de 20 caracteres",
    "accounts.v3.detail.form.continue": "Continuar",
    "accounts.v3.detail.tag.operative": "Operativa",
    "accounts.v3.detail.availableBalance": "Saldo disponible",
    "accounts.v3.detail.realBalance": "Saldo real",
    "accounts.v3.detail.balanceChart.show": "Desplegar",
    "accounts.v3.detail.balanceChart.hide": "Ocultar",
    "accounts.v3.detail.movements.latestMovements": "Últimos movimientos",
    "accounts.v3.detail.movements.pendingMovements": "Pendientes",
    "accounts.v3.detail.movements.searchBy": "Buscar por",
    "accounts.v3.detail.movements.amount": "Monto",
    "accounts.v3.detail.movements.amountFrom": "Monto desde",
    "accounts.v3.detail.movements.amountTo": "Monto hasta",
    "accounts.v3.detail.movements.filter": "Filtrar",
    "accounts.v3.detail.movements.clearFilters": "Limpiar filtros",
    "accounts.v3.detail.movements.seeFilters": "Ver filtros",
    "accounts.v3.detail.movements.hideFilters": "Ocultar filtros",
    "accounts.v3.detail.movements.activeFilters": "Filtros activos",
    "accounts.v3.detail.movements.export": "Descargar movimientos",
    "accounts.v3.detail.movements.exportPdf": "PDF",
    "accounts.v3.detail.movements.exportXls": "XLS",
    "accounts.v3.detail.movements.exportCsv": "CSV",
    "accounts.movements.findBy.period.label": "Periodo",
    "accounts.movements.findBy.amount.label": "Monto",
    "accounts.movements.findBy.detail.label": "Concepto",
    "accounts.movements.findBy.voucher.label": "Comprobante",
    "accounts.movements.findBy.longDetail.label": "Detalle",
    "accounts.movements.findBy.operations.label": "Operaciones",
    "accounts.movements.findBy.type.label.credit": "Crédito",
    "accounts.movements.findBy.type.label.debit": "Débito",
    "accounts.movements.findBy.type.label.all": "Todos",
    "accounts.v3.detail.movements.period": "Periodo",
    "accounts.v3.detail.movements.detail": "Concepto",
    "accounts.v3.detail.movements.voucher": "Comprobante",
    "accounts.v3.detail.movements.longDetail": "Detalle",
    "accounts.v3.detail.movements.operations": "Operaciones",
    "accounts.v3.detail.movements.dateRange": "Período",
    "accounts.v3.detail.movements.empty": "No hay movimientos para mostrar",
    "accounts.v3.detail.extracts.year": "Año",
    "accounts.v3.detail.extracts.month": "Mes",
};

const ACCOUNT_DETAIL_UI_KEY_TO_I18N = {
    movements: "accounts.v3.detail.tab.movements",
    information: "accounts.v3.detail.tab.information",
    extracts: "accounts.v3.detail.tab.extracts",
    "title.edit": "accounts.v3.detail.title.edit",
    "title.edit.form.name": "accounts.v3.detail.title.edit.form.name",
    "title.edit.form.alias": "accounts.v3.detail.title.edit.form.alias",
    "form.account.name": "accounts.v3.detail.form.account.name",
    "form.account.alias": "accounts.v3.detail.form.account.alias",
    "form.continue": "accounts.v3.detail.form.continue",
    "tag.operative": "accounts.v3.detail.tag.operative",
    availableBalance: "accounts.v3.detail.availableBalance",
    realBalance: "accounts.v3.detail.realBalance",
    showBalanceChart: "accounts.v3.detail.balanceChart.show",
    hideBalanceChart: "accounts.v3.detail.balanceChart.hide",
    empty: "accounts.v3.detail.movements.empty",
    latestMovements: "accounts.v3.detail.movements.latestMovements",
    pendingMovements: "accounts.v3.detail.movements.pendingMovements",
    searchBy: "accounts.v3.detail.movements.searchBy",
    amount: "accounts.movements.findBy.amount.label",
    amountFrom: "accounts.v3.detail.movements.amountFrom",
    amountTo: "accounts.v3.detail.movements.amountTo",
    filter: "accounts.v3.detail.movements.filter",
    clearFilters: "accounts.v3.detail.movements.clearFilters",
    seeFilters: "accounts.v3.detail.movements.seeFilters",
    hideFilters: "accounts.v3.detail.movements.hideFilters",
    activeFilters: "accounts.v3.detail.movements.activeFilters",
    export: "accounts.v3.detail.movements.export",
    exportPdf: "accounts.v3.detail.movements.exportPdf",
    exportXls: "accounts.v3.detail.movements.exportXls",
    exportCsv: "accounts.v3.detail.movements.exportCsv",
    period: "accounts.movements.findBy.period.label",
    detail: "accounts.movements.findBy.detail.label",
    voucher: "accounts.movements.findBy.voucher.label",
    longDetail: "accounts.movements.findBy.longDetail.label",
    operations: "accounts.movements.findBy.operations.label",
    movementTypeCredit: "accounts.movements.findBy.type.label.credit",
    movementTypeDebit: "accounts.movements.findBy.type.label.debit",
    movementTypeAll: "accounts.movements.findBy.type.label.all",
    dateRange: "accounts.v3.detail.movements.dateRange",
    year: "accounts.v3.detail.extracts.year",
    month: "accounts.v3.detail.extracts.month",
};

/** Etiquetas del resumen donut/leyenda en saldos proyectados v3. */
const PROJECTED_BALANCE_SUMMARY_FALLBACK = {
    "accounts.v3.projected.todayLabel": "Saldo de hoy",
    "accounts.v3.projected.balance24Label": "Saldo en 24hs",
    "accounts.v3.projected.balance48Label": "Saldo en 48hs",
};

const PROJECTED_BALANCE_SUMMARY_UI_KEY_TO_I18N = {
    todayLabel: "accounts.v3.projected.todayLabel",
    balance24Label: "accounts.v3.projected.balance24Label",
    balance48Label: "accounts.v3.projected.balance48Label",
};

/** Etiquetas de cards y empty state en saldos proyectados v3. */
const PROJECTED_BALANCE_LABEL_FALLBACK = {
    "accounts.v3.projected.movementDate": "Fecha del movimiento",
    "accounts.v3.projected.valueDate": "Fecha valor",
    "accounts.v3.projected.voucher": "Comprobante",
    "accounts.v3.projected.detail": "Detalle",
    "accounts.v3.projected.amount": "Importe",
    "accounts.v3.projected.movements.empty": "No hay movimientos diferidos para mostrar",
};

const PROJECTED_BALANCE_UI_KEY_TO_I18N = {
    movementDate: "accounts.v3.projected.movementDate",
    valueDate: "accounts.v3.projected.valueDate",
    voucher: "accounts.v3.projected.voucher",
    detail: "accounts.v3.projected.detail",
    amount: "accounts.v3.projected.amount",
    empty: "accounts.v3.projected.movements.empty",
};

/** Etiquetas de la página saldos históricos v3 (`AccountHistoricBalanceV3Page`). */
const HISTORIC_BALANCE_LABEL_FALLBACK = {
    "accounts.v3.historic.dateRange": "Fecha de ejecución",
    "accounts.v3.historic.filter": "Filtrar",
    "accounts.v3.historic.clearFilters": "Limpiar filtros",
    "accounts.v3.historic.export": "Descargar",
    "accounts.v3.historic.exportPdf": "PDF",
    "accounts.v3.historic.exportXls": "XLS",
    "accounts.v3.historic.exportCsv": "CSV",
    "accounts.v3.historic.queryDate": "Fecha de consulta",
    "accounts.v3.historic.queryTime": "Hora de consulta",
    "accounts.v3.historic.balanceLabel": "Saldo real",
    "accounts.v3.historic.empty": "No hay saldos para el período seleccionado",
};

const HISTORIC_BALANCE_UI_KEY_TO_I18N = {
    dateRange: "accounts.v3.historic.dateRange",
    filter: "accounts.v3.historic.filter",
    clearFilters: "accounts.v3.historic.clearFilters",
    export: "accounts.v3.historic.export",
    exportPdf: "accounts.v3.historic.exportPdf",
    exportXls: "accounts.v3.historic.exportXls",
    exportCsv: "accounts.v3.historic.exportCsv",
    queryDate: "accounts.v3.historic.queryDate",
    queryTime: "accounts.v3.historic.queryTime",
    balanceLabel: "accounts.v3.historic.balanceLabel",
    empty: "accounts.v3.historic.empty",
};

/** Etiquetas de la página Mis cuentas v3 (`AccountsV3Page` + header shortcuts). */
const ACCOUNTS_V3_LABEL_FALLBACK = {
    "accounts.page.header": "Mis cuentas",
    "accounts.page.newAccount": "Nueva cuenta",
    "accounts.page.balance.download": "Saldos",
    "accounts.v3.balance.download": "Saldos de cuenta",
    "accounts.v3.extract.fundcorp": "Resúmenes Fundcorp",
    "accounts.v3.extract.metrocorp": "Resúmenes Metrocorp",
    "dashboard.v3.shortcut.transfers": "Transferir",
    "dashboard.v3.shortcut.fundSubscribe": "Suscribir fondo",
    "dashboard.v3.shortcut.fundRescue": "Rescatar fondo",
    "fund.operation.subscribe": "Suscribir",
    "fund.operation.rescue": "Rescatar",
};

const TRANSFER_EXECUTE_LABEL_FALLBACK = {
    "tab.internal": "A mis cuentas",
    "tab.thirdParties": "A otras cuentas",
    "tab.toContact": "A un contacto",
    pageTitle: "Cursar una transferencia",
    templates: "Plantillas guardadas",
    goBack: "volver",
};

/** @param {string} key */
function getWithFallback(key, fallbackMap) {
    const t = i18n.get(key, null);
    if (t != null && !isMissingI18nKey(t)) {
        return t;
    }
    return fallbackMap[key] ?? key;
}

/**
 * Etiquetas del rail lateral / bottom nav / submenús (`buildPrivateNavItems`).
 * Usa `i18n.get` con default desde {@link NAV_RAIL_LABEL_FALLBACK} cuando el store no trae la clave.
 *
 * @param {string} key
 * @returns {string}
 */
export function getNavLabel(key) {
    const fb = NAV_RAIL_LABEL_FALLBACK[key];
    return i18n.get(key, fb !== undefined ? fb : null);
}

/**
 * Etiquetas de acciones y header en `/v3/accounts`.
 *
 * @param {string} key
 * @returns {string}
 */
export function getAccountsV3Label(key) {
    return getWithFallback(key, ACCOUNTS_V3_LABEL_FALLBACK);
}

/**
 * Etiquetas del shell de cursar transferencia v3 (tabs, header).
 *
 * @param {string} key
 * @returns {string}
 */
export function getTransferExecuteLabels(key) {
    if (key === "tab.internal") {
        return getTransferInternalTitleLabel();
    }

    const i18nMap = {
        "tab.thirdParties": "transfers.form.thirdParties.title",
        "tab.toContact": "transfers.form.contact.title",
        pageTitle: "transfers.make.transfer.title",
        templates: "transfers.templates",
        goBack: "header.goBack.aria",
    };
    const i18nKey = i18nMap[key];
    if (i18nKey) {
        return getWithFallback(i18nKey, TRANSFER_EXECUTE_LABEL_FALLBACK);
    }
    return getWithFallback(key, TRANSFER_EXECUTE_LABEL_FALLBACK);
}

/**
 * Etiquetas de `AccountDetail` v3 (tabs, header, filtros movimientos/extractos).
 *
 * @param {string} key
 * @returns {string}
 */
export function getAccountDetailLabels(key) {
    const i18nKey = ACCOUNT_DETAIL_UI_KEY_TO_I18N[key];
    return i18nKey ? getWithFallback(i18nKey, ACCOUNT_DETAIL_LABEL_FALLBACK) : key;
}

/**
 * Etiquetas del resumen (saldo hoy / 24hs / 48hs) en `AccountsProjectedBalance`.
 *
 * @param {string} key
 * @returns {string}
 */
export function getProjectedBalanceSummaryLabels(key) {
    const i18nKey = PROJECTED_BALANCE_SUMMARY_UI_KEY_TO_I18N[key];
    return i18nKey ? getWithFallback(i18nKey, PROJECTED_BALANCE_SUMMARY_FALLBACK) : key;
}

/**
 * Etiquetas de cards de movimientos diferidos y empty state en saldos proyectados v3.
 *
 * @param {string} key
 * @returns {string}
 */
export function getProjectedBalanceLabels(key) {
    const i18nKey = PROJECTED_BALANCE_UI_KEY_TO_I18N[key];
    return i18nKey ? getWithFallback(i18nKey, PROJECTED_BALANCE_LABEL_FALLBACK) : key;
}

/**
 * Etiquetas de filtros, export, meta y cards en saldos históricos v3.
 *
 * @param {string} key
 * @returns {string}
 */
export function getHistoricBalanceLabels(key) {
    const i18nKey = HISTORIC_BALANCE_UI_KEY_TO_I18N[key];
    return i18nKey ? getWithFallback(i18nKey, HISTORIC_BALANCE_LABEL_FALLBACK) : key;
}

/** @deprecated Usar {@link getAccountDetailLabels} */
export function getAccountDetailTabLabels(tab) {
    return getAccountDetailLabels(tab);
}

export function getGreetingTexts(key) {
    return getWithFallback(key, GREETING_FALLBACK);
}

export function getHeaderLabels(key) {
    return getWithFallback(key, HEADER_LABEL_FALLBACK);
}

export function getHeaderUserMenuLabels(key) {
    return getWithFallback(key, USER_MENU_FALLBACK);
}

export function getMenuLabels(key) {
    return getWithFallback(key, MENU_LABEL_FALLBACK);
}

/**
 * @param {string} key
 * @param {Record<string, string | number>} params
 */
export function getMenuTextParams(key, params) {
    let template = i18n.get(key, null);
    if (template == null || isMissingI18nKey(template)) {
        template = MENU_TEXT_FALLBACK[key] ?? "";
    }
    let out = template;
    Object.entries(params).forEach(([k, v]) => {
        out = out.replace(new RegExp(`\\{\\{${k}\\}\\}`, "g"), String(v));
    });
    return out;
}

const DASHBOARD_LABEL_FALLBACK = {
    "dashboard.accounts.sectionTitle": "Mis cuentas",
    "dashboard.pending.sectionTitle": "Acciones pendientes de firma",
    "dashboard.shortcuts.regionAria": "Accesos directos",
    "dashboard.shortcuts.moreLabel": "Más",
    "dashboard.shortcuts.moreShortcutsAria": "Más accesos directos",
    "dashboard.shortcuts.drawerTitle": "Acciones rápidas",
    "dashboard.shortcuts.drawerIntro": "Tenés estas acciones disponibles para operar de inmediato.",
    "dashboard.carousel.regionAria": "Carrusel",
    "dashboard.actions.consolidatedPosition": "Posición consolidada",
    "dashboard.actions.consolidatedPositionAria": "Posición consolidada",
    "dashboard.actions.editDesktop": "Editar escritorio",
    "dashboard.actions.editDesktopAria": "Editar escritorio",
    "dashboard.accounts.seeMore": "Ver más",
    "dashboard.accounts.seeMoreAria": "Ver más",
    "dashboard.pending.seeMore": "Ver más",
    "dashboard.pending.seeMoreAria": "Ver más",
    "dashboard.pending.sectionEmpty": "Sin acciones pendientes de firma.",
    "dashboard.cardAccount.copyCbu": "Copiar CBU",
    "dashboard.cardAccount.copyCbuAria": "Copiar CBU",
    "dashboard.cardAccount.statements": "Resúmenes",
    "dashboard.cardAccount.statementsAria": "Resúmenes",
    "dashboard.cardAccount.transfer": "Transferir",
    "dashboard.cardAccount.transferAria": "Transferir",
    "dashboard.v3.shortcut.emitEcheq": "Emitir Echeq",
    "dashboard.v3.shortcut.temporaryLimitIncrease": "Aumento de límite temporal",
    "dashboard.cardPending.creationPrefix": "Creación:",
    "dashboard.cardEcheq.creationPrefix": "Recibido:",
    "dashboard.cardFunds.creationPrefix": "Valor cuotaparte:",
    "dashboard.cardFixedTerm.creationPrefix": "Vence:",
    "dashboard.cardMetrocorp.creationPrefix": "Movimiento:",
    "dashboard.cardFinancing.creationPrefix": "Próximo pago:",
    "dashboard.cardFunds.field.shareholderAccount": "Cuenta cuotapartista",
    "dashboard.cardFunds.field.accountNumber": "Nº de cuenta",
    "dashboard.cardFunds.field.fundName": "Nombre del fondo",
    "dashboard.cardFunds.field.fundClass": "Clase",
    "dashboard.cardFunds.field.shareCount": "Cuotapartes",
    "dashboard.cardFunds.field.amount": "Monto",
    "dashboard.cardFixedTerm.field.maturityDate": "Fecha de Vencimiento",
    "dashboard.cardFixedTerm.field.annualRate": "TNA",
    "dashboard.cardFixedTerm.field.investedAmount": "Monto invertido",
    "dashboard.cardFixedTerm.field.certificateNumber": "Certificado",
    "dashboard.cardFixedTerm.field.amountToReceive": "Monto a cobrar",
    "dashboard.cardFixedTerm.field.autoRenewal": "Renovación automática",
    "dashboard.cardFixedTerm.autoRenewal": "Auto",
    "dashboard.cardEcheq.field.paymentDate": "Fecha de pago",
    "dashboard.cardEcheq.field.sameHolder": "Titular",
    "dashboard.cardEcheq.sameHolderYes": "Mismo titular",
    "dashboard.cardEcheq.sameHolderNo": "Distinto titular",
    "dashboard.cardEcheq.toOrder": "A la orden",
    "dashboard.cardEcheq.notToOrder": "No a la orden",
    "dashboard.cardEcheq.field.order": "Orden",
    "dashboard.cardEcheq.field.status": "Estado",
    "dashboard.cardFinancing.field.activeCount": "Vigentes",
    "dashboard.cardFinancing.field.name": "Nombre",
    "dashboard.cardFinancing.field.debtBalance": "Saldo deuda",
    "dashboard.cardMetrocorp.field.accountNumber": "Comitente",
    "dashboard.cardMetrocorp.field.cashValue": "Efectivo",
    "dashboard.cardMetrocorp.field.fixedIncomeValue": "Renta fija",
    "dashboard.cardMetrocorp.field.variableIncomeValue": "Renta variable",
    "dashboard.cardMetrocorp.field.otherValue": "Otros",
    "dashboard.cardMetrocorp.emptyValues": "No hay información disponible",
    "dashboard.editDrawer.title": "Widgets disponibles",
    "dashboard.editDrawer.emptyList": "No hay widgets",
    "dashboard.editDrawer.confirm": "Confirmar",
    "dashboard.editDrawer.confirmAria": "Confirmar",
};

const BIOMETRIC_WIDGET_FALLBACK = {
    "widgets.biometricIdentification.title": "Este dispositivo permite identificación biométrica",
    "widgets.biometricIdentification.body":
        "La configuración de los datos biométricos permite fácil acceso a la aplicación y mayor seguridad para el inicio de sesión.",
    "widgets.biometricIdentification.dontShowAgain": "No volver a mostrar este mensaje",
    "widgets.biometricIdentification.configureNow": "Configurar ahora",
    "settings.biometrics.acceptTermsConditions.label": "Aceptar TyC",
    "global.close": "Cerrar",
};

const DASHBOARD_TEXT_TEMPLATES_FALLBACK = {
    "dashboard.editDrawer.helperMaxWidgets":
        "Podés visualizar hasta un máximo de {{max}} widgets en formato tab simultáneamente.",
    "dashboard.editDrawer.selectedCount": "Widgets seleccionados: {{count}} de {{max}}",
    "dashboard.draggable.orderBadgeAria": "Orden entre seleccionados: {{order}}",
};

/** @param {string} key */
export function getDashboardLabels(key) {
    return getWithFallback(key, DASHBOARD_LABEL_FALLBACK);
}

/**
 * @param {string} key
 * @param {Record<string, string | number>} params
 */
/**
 * Textos del widget de identificación biométrica (UI kit `BiometricIdentification`).
 * @returns {{
 *   title: string,
 *   body: string,
 *   dontShowAgain: string,
 *   secondaryAcceptTerms: string,
 *   configureNow: string,
 *   close: string,
 * }}
 */
export function getBiometricWidgetLabels() {
    return {
        title: getWithFallback("widgets.biometricIdentification.title", BIOMETRIC_WIDGET_FALLBACK),
        body: getWithFallback("widgets.biometricIdentification.body", BIOMETRIC_WIDGET_FALLBACK),
        dontShowAgain: getWithFallback("widgets.biometricIdentification.dontShowAgain", BIOMETRIC_WIDGET_FALLBACK),
        secondaryAcceptTerms: getWithFallback(
            "settings.biometrics.acceptTermsConditions.label",
            BIOMETRIC_WIDGET_FALLBACK,
        ),
        configureNow: getWithFallback("widgets.biometricIdentification.configureNow", BIOMETRIC_WIDGET_FALLBACK),
        close: getWithFallback("global.close", BIOMETRIC_WIDGET_FALLBACK),
    };
}

export function getDashboardTextParams(key, params) {
    let template = i18n.get(key, null);
    if (template == null || isMissingI18nKey(template)) {
        template = DASHBOARD_TEXT_TEMPLATES_FALLBACK[key] ?? "";
    }
    let out = template;
    Object.entries(params).forEach(([k, v]) => {
        out = out.replace(new RegExp(`\\{\\{${k}\\}\\}`, "g"), String(v));
    });
    return out;
}
